import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Clock3, History, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  fetchRetailCatalog,
  fetchRetailInventory,
  fetchRetailInventoryLedger,
  submitRetailInventoryReceipt,
  type RetailInventoryLedgerEntry,
} from "@/lib/api/retail";
import type { RetailInventoryLevel, RetailProduct } from "@/features/retail/types/datasets";
import { useLocaleFormatter } from "@/lib/i18n/useLocaleFormatter";

type RetailReceiveStockDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  defaultProductId?: string | null;
  defaultLevelId?: string | null;
};

type InventoryOption = {
  level: RetailInventoryLevel;
  product: RetailProduct | null;
  available: number;
};

type ReceivingStep = "select" | "details" | "review";

const DEFAULT_QUANTITY = "5";

export const RetailReceiveStockDialog = ({
  open,
  onOpenChange,
  onSuccess,
  defaultProductId = null,
  defaultLevelId = null,
}: RetailReceiveStockDialogProps) => {
  const { t } = useTranslation(["common", "retail"]);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { formatRelativeTime, formatNumber } = useLocaleFormatter();

  const catalogQuery = useQuery({
    queryKey: ["retail", "catalog"],
    queryFn: ({ signal }) => fetchRetailCatalog({ signal }),
    staleTime: 5 * 60 * 1000,
  });

  const inventoryQuery = useQuery({
    queryKey: ["retail", "inventory"],
    queryFn: ({ signal }) => fetchRetailInventory({ signal }),
    staleTime: 15 * 1000,
  });

  const [step, setStep] = useState<ReceivingStep>("select");
  const [selectedProductId, setSelectedProductId] = useState<string | null>(defaultProductId);
  const [selectedLevelId, setSelectedLevelId] = useState<string | null>(defaultLevelId);
  const [quantity, setQuantity] = useState(DEFAULT_QUANTITY);
  const [expectedQuantity, setExpectedQuantity] = useState(DEFAULT_QUANTITY);
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!open) {
      setStep("select");
      setQuantity(DEFAULT_QUANTITY);
      setExpectedQuantity(DEFAULT_QUANTITY);
      setReference("");
      setNote("");
      setSelectedProductId(defaultProductId ?? null);
      setSelectedLevelId(defaultLevelId ?? null);
    } else {
      setSelectedProductId(defaultProductId ?? null);
      setSelectedLevelId(defaultLevelId ?? null);
    }
  }, [open, defaultProductId, defaultLevelId]);

  const productById = useMemo(() => {
    const map = new Map<string, RetailProduct>();
    for (const product of catalogQuery.data ?? []) {
      map.set(product.id, product);
    }
    return map;
  }, [catalogQuery.data]);

  const inventoryByProduct = useMemo(() => {
    const map = new Map<string, InventoryOption[]>();
    for (const level of inventoryQuery.data ?? []) {
      const product = productById.get(level.productId) ?? null;
      const available = Math.max(0, level.onHand - level.allocated);
      const list = map.get(level.productId) ?? [];
      list.push({ level, product, available });
      map.set(level.productId, list);
    }
    return map;
  }, [inventoryQuery.data, productById]);

  const productOptions = useMemo(
    () => (catalogQuery.data ?? []).slice().sort((left, right) => left.name.localeCompare(right.name)),
    [catalogQuery.data],
  );

  const levelOptions = useMemo(() => {
    if (!selectedProductId) {
      return [] as InventoryOption[];
    }
    return inventoryByProduct.get(selectedProductId) ?? [];
  }, [inventoryByProduct, selectedProductId]);

  useEffect(() => {
    if (levelOptions.length === 0) {
      setSelectedLevelId(null);
      return;
    }
    if (selectedLevelId && levelOptions.some((option) => option.level.id === selectedLevelId)) {
      return;
    }
    setSelectedLevelId(levelOptions[0]?.level.id ?? null);
  }, [levelOptions, selectedLevelId]);

  const selectedOption = levelOptions.find((option) => option.level.id === selectedLevelId) ?? null;

  const ledgerQueryKey = useMemo(
    () => ["retail", "inventory", "ledger", selectedLevelId ?? "-"],
    [selectedLevelId],
  );
  const ledgerQuery = useQuery({
    queryKey: ledgerQueryKey,
    enabled: open && Boolean(selectedLevelId),
    queryFn: ({ signal }) =>
      fetchRetailInventoryLedger({ levelId: selectedLevelId ?? undefined, limit: 10, signal }),
    staleTime: 20 * 1000,
  });

  const receiptMutation = useMutation({
    mutationFn: submitRetailInventoryReceipt,
    onSuccess: () => {
      toast({
        title: t("retail.receiving.toastSuccess", { defaultValue: "Stock recorded" }),
        description: t("retail.receiving.toastDescription", {
          defaultValue: "The on-hand count was updated successfully.",
        }),
      });
      queryClient.invalidateQueries({ queryKey: ["retail", "inventory"] });
      queryClient.invalidateQueries({ queryKey: ["retail", "dashboard"] });
      queryClient.invalidateQueries({ queryKey: ledgerQueryKey });
      setStep("select");
      setQuantity(DEFAULT_QUANTITY);
      setExpectedQuantity(DEFAULT_QUANTITY);
      setReference("");
      setNote("");
      onSuccess?.();
      onOpenChange(false);
    },
    onError: (error: unknown) => {
      toast({
        title: t("retail.receiving.toastError", { defaultValue: "Unable to update stock" }),
        description:
          error instanceof Error
            ? error.message
            : t("status.tryAgain", { defaultValue: "Please try again." }),
        variant: "destructive",
      });
    },
  });

  const isLoading = catalogQuery.isLoading || inventoryQuery.isLoading;
  const hasError = catalogQuery.isError || inventoryQuery.isError;

  const parseQuantity = (value: string) => {
    if (/^\s*$/.test(value)) {
      return Number.NaN;
    }
    return Number.parseFloat(value);
  };

  const validateDetails = () => {
    const parsedQuantity = parseQuantity(quantity);
    if (!selectedLevelId) {
      toast({
        title: t("retail.quickActions.receive.missingLocationTitle", {
          defaultValue: "Location required",
        }),
        description: t("retail.quickActions.receive.missingLocationDescription", {
          defaultValue: "Choose where the stock should be received.",
        }),
        variant: "destructive",
      });
      return false;
    }

    if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) {
      toast({
        title: t("retail.quickActions.receive.invalidQuantityTitle", {
          defaultValue: "Invalid quantity",
        }),
        description: t("retail.quickActions.receive.invalidQuantityDescription", {
          defaultValue: "Enter a positive number of units to add.",
        }),
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handleNext = () => {
    if (validateDetails()) {
      setStep("review");
    }
  };

  const handleSubmit = () => {
    if (!validateDetails() || !selectedLevelId) {
      return;
    }

    const parsedQuantity = parseQuantity(quantity);
    const parsedExpected = parseQuantity(expectedQuantity);

    receiptMutation.mutate({
      levelId: selectedLevelId,
      quantity: parsedQuantity,
      expectedQuantity:
        Number.isFinite(parsedExpected) && parsedExpected > 0 ? parsedExpected : null,
      reference: reference.trim().length > 0 ? reference.trim() : null,
      note: note.trim().length > 0 ? note.trim() : null,
    });
  };

  const handleClose = (next: boolean) => {
    if (!next) {
      setStep("select");
      setQuantity(DEFAULT_QUANTITY);
      setExpectedQuantity(DEFAULT_QUANTITY);
      setReference("");
      setNote("");
    }
    onOpenChange(next);
  };

  const renderLedgerHistory = () => {
    if (!open || !selectedLevelId) {
      return null;
    }

    if (ledgerQuery.isLoading) {
      return (
        <div className="space-y-2">
          {[1, 2, 3].map((index) => (
            <Skeleton key={index} className="h-10 w-full" />
          ))}
        </div>
      );
    }

    if (ledgerQuery.isError || !ledgerQuery.data || ledgerQuery.data.length === 0) {
      return (
        <p className="text-sm text-muted-foreground">
          {t("retail.receiving.history.empty", {
            defaultValue: "No recent inventory events recorded.",
          })}
        </p>
      );
    }

    return (
      <div className="space-y-2">
        {ledgerQuery.data.map((entry: RetailInventoryLedgerEntry) => {
          const formattedDelta = formatNumber(entry.quantityDelta);
          const deltaDisplay =
            entry.quantityDelta > 0 && formattedDelta !== "--"
              ? `+${formattedDelta}`
              : formattedDelta;

          return (
            <div key={entry.id} className="rounded-lg border border-muted bg-muted/40 p-3 text-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="uppercase">
                    {t(`retail.ledger.event.${entry.eventType}`, {
                      defaultValue: entry.eventType,
                    })}
                  </Badge>
                  <span className="font-semibold">{deltaDisplay}</span>
                </div>
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock3 className="h-3 w-3" />
                  {formatRelativeTime(entry.occurredAt)}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span>
                  {t("retail.ledger.resultingOnHand", {
                    defaultValue: "On hand: {{value}}",
                    value: formatNumber(entry.resultingOnHand),
                  })}
                </span>
                {entry.reference ? (
                  <span>
                    {t("retail.ledger.reference", {
                      defaultValue: "Ref {{value}}",
                      value: entry.reference,
                    })}
                  </span>
                ) : null}
                {entry.note ? <span>{entry.note}</span> : null}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderStep = () => {
    if (hasError) {
      return (
        <div className="space-y-3 text-sm text-muted-foreground">
          <p>{t("retail.receiving.loadError", { defaultValue: "Inventory data is unavailable." })}</p>
          <Button variant="secondary" onClick={() => {
            void queryClient.invalidateQueries({ queryKey: ["retail", "catalog"] });
            void queryClient.invalidateQueries({ queryKey: ["retail", "inventory"] });
          }}>
            {t("retail.receiving.retry", { defaultValue: "Retry" })}
          </Button>
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      );
    }

    switch (step) {
      case "details":
        return (
          <div className="space-y-6">
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="receiving-product">
                  {t("retail.quickActions.receive.productLabel", { defaultValue: "Product" })}
                </Label>
                <Select
                  value={selectedProductId ?? ""}
                  onValueChange={(value) => {
                    setSelectedProductId(value);
                    setStep("details");
                  }}
                  disabled={receiptMutation.isLoading}
                >
                  <SelectTrigger id="receiving-product">
                    <SelectValue
                      placeholder={t("retail.quickActions.receive.productPlaceholder", {
                        defaultValue: "Select a product",
                      })}
                    />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {productOptions.map((product) => (
                      <SelectItem key={product.id} value={product.id}>
                        <div className="flex flex-col">
                          <span>{product.name}</span>
                          <span className="text-xs text-muted-foreground">{product.sku}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {levelOptions.length > 1 ? (
                <div className="grid gap-2">
                  <Label htmlFor="receiving-level">
                    {t("retail.receiving.locationLabel", { defaultValue: "Location" })}
                  </Label>
                  <Select
                    value={selectedLevelId ?? ""}
                    onValueChange={(value) => setSelectedLevelId(value)}
                    disabled={receiptMutation.isLoading}
                  >
                    <SelectTrigger id="receiving-level">
                      <SelectValue
                        placeholder={t("retail.receiving.locationPlaceholder", {
                          defaultValue: "Select a location",
                        })}
                      />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {levelOptions.map((option) => (
                        <SelectItem key={option.level.id} value={option.level.id}>
                          <div className="flex flex-col">
                            <span>{option.level.locationName || t("retail.receiving.unknownLocation", { defaultValue: "Unassigned" })}</span>
                            <span className="text-xs text-muted-foreground">
                              {t("retail.receiving.available", {
                                defaultValue: "Available: {{count}}",
                                count: formatNumber(option.available),
                              })}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
              <div className="grid gap-2">
                <Label htmlFor="receiving-quantity">
                  {t("retail.receiving.quantityLabel", { defaultValue: "Received quantity" })}
                </Label>
                <Input
                  id="receiving-quantity"
                  inputMode="numeric"
                  value={quantity}
                  onChange={(event) => setQuantity(event.target.value)}
                  disabled={receiptMutation.isLoading}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="receiving-expected">
                  {t("retail.receiving.expectedLabel", { defaultValue: "Expected quantity" })}
                </Label>
                <Input
                  id="receiving-expected"
                  inputMode="numeric"
                  value={expectedQuantity}
                  onChange={(event) => setExpectedQuantity(event.target.value)}
                  disabled={receiptMutation.isLoading}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="receiving-reference">
                  {t("retail.receiving.referenceLabel", {
                    defaultValue: "Reference (PO, ticket)",
                  })}
                </Label>
                <Input
                  id="receiving-reference"
                  value={reference}
                  onChange={(event) => setReference(event.target.value)}
                  placeholder={t("retail.receiving.referencePlaceholder", {
                    defaultValue: "PO-12345",
                  })}
                  disabled={receiptMutation.isLoading}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="receiving-note">
                  {t("retail.receiving.noteLabel", { defaultValue: "Notes" })}
                </Label>
                <Textarea
                  id="receiving-note"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  rows={3}
                  disabled={receiptMutation.isLoading}
                />
              </div>
            </div>
            <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
              <Button
                type="button"
                variant="ghost"
                className="w-full sm:w-auto"
                onClick={() => setStep("select")}
                disabled={receiptMutation.isLoading}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t("retail.receiving.back", { defaultValue: "Back" })}
              </Button>
              <Button
                type="button"
                className="w-full sm:w-auto"
                onClick={handleNext}
                disabled={receiptMutation.isLoading || !selectedOption}
              >
                {t("retail.receiving.review", { defaultValue: "Review" })}
              </Button>
            </DialogFooter>
          </div>
        );
      case "review": {
        const parsedQuantity = parseQuantity(quantity);
        const reviewQuantityDisplay = Number.isFinite(parsedQuantity)
          ? formatNumber(parsedQuantity)
          : quantity.trim() ||
            t("retail.inventory.table.notAvailable", { defaultValue: "-" });
        const parsedExpected = parseQuantity(expectedQuantity);
        const reviewExpectedDisplay = Number.isFinite(parsedExpected)
          ? formatNumber(parsedExpected)
          : expectedQuantity.trim() ||
            t("retail.inventory.table.notAvailable", { defaultValue: "-" });
        const referenceDisplay =
          reference.trim() ||
          t("retail.inventory.table.notAvailable", { defaultValue: "-" });
        const noteDisplay =
          note.trim() ||
          t("retail.inventory.table.notAvailable", { defaultValue: "-" });

        return (
          <div className="space-y-6">
            <div className="rounded-lg border border-muted bg-muted/30 p-4 text-sm">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h3 className="text-base font-semibold">
                    {selectedOption?.product?.name ?? selectedOption?.level.id}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {selectedOption?.level.locationName || t("retail.receiving.unknownLocation", {
                      defaultValue: "Unassigned location",
                    })}
                  </p>
                </div>
                <Badge variant="secondary" className="w-fit uppercase">
                  {t("retail.receiving.review", { defaultValue: "Review" })}
                </Badge>
              </div>
              <dl className="mt-4 grid gap-3 text-xs sm:grid-cols-2">
                <div>
                  <dt className="text-muted-foreground">
                    {t("retail.receiving.reviewReceived", { defaultValue: "Received" })}
                  </dt>
                  <dd className="font-semibold">{reviewQuantityDisplay}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">
                    {t("retail.receiving.reviewExpected", { defaultValue: "Expected" })}
                  </dt>
                  <dd className="font-semibold">{reviewExpectedDisplay}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">
                    {t("retail.receiving.reviewReference", { defaultValue: "Reference" })}
                  </dt>
                  <dd className="font-semibold">{referenceDisplay}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">
                    {t("retail.receiving.reviewNote", { defaultValue: "Notes" })}
                  </dt>
                  <dd className="font-semibold">{noteDisplay}</dd>
                </div>
              </dl>
            </div>
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2 sm:flex-nowrap">
                <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  {t("retail.receiving.recentHistory", { defaultValue: "Recent history" })}
                </h4>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full justify-center sm:w-auto"
                  onClick={() => ledgerQuery.refetch()}
                >
                  <History className="mr-2 h-4 w-4" />
                  {t("retail.receiving.refreshHistory", { defaultValue: "Refresh" })}
                </Button>
              </div>
              {renderLedgerHistory()}
            </div>
            <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
              <Button
                type="button"
                variant="ghost"
                className="w-full sm:w-auto"
                onClick={() => setStep("details")}
                disabled={receiptMutation.isLoading}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t("retail.receiving.back", { defaultValue: "Back" })}
              </Button>
              <Button
                type="button"
                className="w-full sm:w-auto"
                onClick={handleSubmit}
                disabled={receiptMutation.isLoading}
              >
                {receiptMutation.isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("retail.receiving.confirm", { defaultValue: "Confirm receipt" })}
              </Button>
            </DialogFooter>
          </div>
        );
      }
      case "select":
      default:
        return (
          <div className="space-y-6">
            <div className="grid gap-2">
              <Label htmlFor="receiving-product">
                {t("retail.quickActions.receive.productLabel", { defaultValue: "Product" })}
              </Label>
              <Select
                value={selectedProductId ?? ""}
                onValueChange={(value) => {
                  setSelectedProductId(value);
                  setStep("details");
                }}
                disabled={isLoading}
              >
                <SelectTrigger id="receiving-product">
                  <SelectValue
                    placeholder={t("retail.quickActions.receive.productPlaceholder", {
                      defaultValue: "Select a product",
                    })}
                  />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {productOptions.map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      <div className="flex flex-col">
                        <span>{product.name}</span>
                        <span className="text-xs text-muted-foreground">{product.sku}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-sm text-muted-foreground">
              {t("retail.receiving.helper", {
                defaultValue: "Choose a product to begin recording the incoming stock.",
              })}
            </p>
          </div>
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] max-w-2xl overflow-hidden p-0 max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-4rem)]">
        <DialogHeader className="space-y-1 px-4 pt-5 pb-3 sm:px-6">
          <DialogTitle>
            {t("retail.receiving.title", { defaultValue: "Record received stock" })}
          </DialogTitle>
          <DialogDescription>
            {step === "select"
              ? t("retail.receiving.descriptionSelect", {
                  defaultValue: "Find a SKU to start the receiving flow.",
                })
              : step === "details"
              ? t("retail.receiving.descriptionDetails", {
                  defaultValue: "Verify quantities and reference details before posting.",
                })
              : t("retail.receiving.descriptionReview", {
                  defaultValue: "Confirm the receipt and review recent inventory history.",
                })}
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[calc(100vh-13rem)] px-4 pb-5 sm:px-6">
          <div className="space-y-6">{renderStep()}</div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default RetailReceiveStockDialog;
